import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import ShortDateFormat from '../components/ShortDateFormat';
import LoadingSpinner from '../components/LoadingSpinner';

const TechOperationDetails = () => {
  const { id } = useParams();         // operation_scheduler.id
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [op, setOp] = useState(null);       // the operation schedule row
  const [techNotes, setTechNotes] = useState('');
  const [isReady, setIsReady] = useState(false); // show Complete button once notes exist

  useEffect(() => {
    fetchOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOperation = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/operation_crud.php`, {
        params: { getOperationTechDetail: id }
      });
      // expect: ['success', row] or {success:true, data: row}
      let row = null;
      if (Array.isArray(data) && data[0] === 'success') row = data[1] || null;
      else if (data?.success) row = data.data || null;

      if (!row) {
        toast.error('Operation not found', { position: 'top-center' });
        return;
      }
      setOp(row);
      const existing = row.tech_notes || '';
      setTechNotes(existing);
      setIsReady(!!existing);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
        navigate('/login');
      } else if (err.response?.status === 403) {
        toast.error("You don't have access to this area.", { position: 'top-center' });
      } else {
        toast.error('Error loading schedule', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    setLoading(true);
    try {
      const body = {
        status: 'opAddTechNotes',
        data: { id, tech_notes: techNotes }
      };
      const { data } = await axios.post(`${apiUrl}/operation_crud.php`, body, {
        headers: { 'Content-Type': 'application/json' }
      });
      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        setIsReady(true);
        setOp(prev => ({ ...(prev || {}), tech_notes: techNotes }));
        toast.success('Notes saved', { position: 'top-center' });
      } else {
        toast.error(data?.error || 'Failed to save notes', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Error saving notes', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const body = { status: 'opCompleted', data: { id } };
      const { data } = await axios.post(`${apiUrl}/operation_crud.php`, body, {
        headers: { 'Content-Type': 'application/json' }
      });
      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        toast.success('Marked as Completed', { position: 'top-center' });
        // navigate to a tech list page if you have one
        navigate('/t_op_list'); 
        fetchOperation();
      } else {
        toast.error(data?.error || 'Failed to complete', { position: 'top-center' });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Error completing schedule', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !op) return <LoadingSpinner>Loading...</LoadingSpinner>;
  if (!op) return <div className="p-6">No data.</div>;

  return (
    <div className="pb-10">
      <HeadingCp label="Operation Details" />

      <ul role="list" className="divide-y divide-gray-100 p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2">
            {op.description && (
              <p className=" text-sm text-gray-700">
                {op.description}
              </p>
            )}            
          <div className="flex items-center text-sm gap-1">
            <p>{op.schedule_from ? <ShortDateFormat date={op.schedule_from} /> : op.schedule_from}</p>
            <p>-</p>
            <p>{op.schedule_to ? <ShortDateFormat date={op.schedule_to} /> : op.schedule_to}</p>
          </div>
          <div className="flex items-center text-sm gap-1">
            <p>{op.start_time || ''}</p>
            <p>{op.end_time ? `@ ${op.end_time}` : ''}</p>
          </div>
          <div className="text-sm">
            <p className="text-green-700 font-semibold">Tech: {op.tech_name || '-'}</p>
            <p className="text-gray-600">Status: {op.status}</p>
            <p className="text-gray-500 text-xs">Log: {op.date} {op.time_log}</p>
            <br/>
            {op.user_comment && (
              <p className=" text-xs text-yellow-700">
                Coordinator: {op.user_comment}
              </p>
            )}            
            {/* If you later join projects/customers, you can show job site/customer here */}
          </div>
        </div>
      </ul>

      <div className="mt-4">Tech's Notes</div>
      <div className="mb-3">
        <textarea
          value={techNotes || ''}
          onChange={(e) => setTechNotes(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-4 py-2 mb-3"
          rows="4"
        />
        <div className="flex justify-end gap-2">
          <button onClick={handleSaveNotes} className="px-4 py-2 bg-blue-600 text-white rounded">
            Save Notes
          </button>
          {isReady && (
            <button onClick={handleComplete} className="px-4 py-2 bg-green-600 text-white rounded">
              Mark Completed
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechOperationDetails;
